import { useRef, useState } from 'react';
import { getBrands } from 'common/api/search/getBrands';
import {
    Brand,
    searchResultBrandsToBrands,
} from 'common/features/store/duck/search-generic/utils/searchResultBrandsToBrands';
import { useBrandList } from 'common/hooks/useBrandList';
import { CardPropsEarns, convertBrandToCardProps } from 'components/bonus/CardProps';
import { BrandCardSlim } from 'components/brand-card/BrandCardSlim';
import { Render } from 'components/loading-screen/Render';
import { FadeIn } from 'features/wallet/components/WalletCard';
import { useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import React, { useEffect, useMemo } from 'react';
import { SkeletonProps } from 'react-loading-skeleton';
import { useConstructorDefaultParameters } from 'common/api/constructor/useConstructorDefaultParameters';
import { constructorClient } from 'third-party-integrations/constructor';
import { constructorToBrand } from 'common/api/constructor/util/conversion-utils';
import { useRootSelector } from 'common/features/featuresReducer';
import { selectIsConstructorBrowseEnabledWeb } from 'common/features/store/duck/ffs';
import { ConstructorReturnObject } from 'common/api/constructor/util';

export const BrandCardTiles = () => {
    const isConstructorEnabled = useRootSelector(selectIsConstructorBrowseEnabledWeb);

    if (isConstructorEnabled === undefined) {
        return null;
    }

    return isConstructorEnabled ? <BrandCardTilesConstructor /> : <BrandCardTilesCognitive />;
};

const BrandCardTilesCognitive = () => {
    const cardBrandTilesValue = useFirebaseConfigValues('cart_brand_card_tiles', undefined, (v) =>
        v.asString()
    );
    const { data: brands, loadData } = useBrandList<Brand[]>(searchResultBrandsToBrands);
    const isLoading = useRef<boolean>(true);

    const cardBrandTiles = useMemo(() => {
        if (cardBrandTilesValue) {
            return JSON.parse(cardBrandTilesValue)['brandIds'] as number[];
        }
        return [];
    }, [cardBrandTilesValue]);

    useEffect(() => {
        if (cardBrandTiles.length > 0 && isLoading.current) {
            isLoading.current = false;
            const filter = `search.in(Id, '${cardBrandTiles.join(', ')}')`;
            loadData(getBrands)({
                filter,
            });
        }
    }, [loadData, cardBrandTiles]);

    const brandsList = (brands?.map((brand) => convertBrandToCardProps(brand)) || []).slice(0, 5);

    return (
        <Render<SkeletonProps>
            when={Boolean(brands && brands.length > 0)}
            loadingComponentProps={{ className: 'h-36 sm:h-56' }}
        >
            <FadeIn>
                <div className="pt-14 pb-8 text-2xl bg-pale-blue">
                    <div className="text-center mb-8 text-brand-dark px-4 font-semibold">
                        We thought you might also like these brands
                    </div>
                    <div className="flex flex-wrap gap-2 justify-center">
                        {brandsList.map((brand) => (
                            <BrandCardSlim key={brand.id} styles={{}} data={brand} />
                        ))}
                    </div>
                </div>
            </FadeIn>
        </Render>
    );
};

const BrandCardTilesConstructor = () => {
    const [cardBrandList, setCardBrandList] = useState<CardPropsEarns[]>([]);

    const isLoading = useRef<boolean>(true);

    const lastUpdate = useRootSelector((s) => s.store.organization.optionsAsyncState.lastUpdate);
    const homeDetailsLastUpdate = useRootSelector(
        (s) => s.store.home.homeDetailsAsyncState.lastUpdate
    );

    const baseFilters = useConstructorDefaultParameters({ resultsPerPage: 5 });

    useEffect(() => {
        if (isLoading.current && lastUpdate > 0 && homeDetailsLastUpdate > 0) {
            const loadBrands = async () => {
                const result = await constructorClient.browse.getBrowseResults(
                    'group_id',
                    'Filter-IsSponsored',
                    baseFilters.current
                );

                isLoading.current = false;

                if (result?.response?.results?.length) {
                    const results = result.response?.results;

                    const brandsConstructor = constructorToBrand(
                        results as ConstructorReturnObject[]
                    );
                    const brands = searchResultBrandsToBrands({
                        count: brandsConstructor.length,
                        brands: brandsConstructor,
                    });

                    const brandsToCards = brands.map((brand) => convertBrandToCardProps(brand));

                    setCardBrandList(brandsToCards);
                }
            };
            loadBrands();
        }
    }, [baseFilters, lastUpdate, homeDetailsLastUpdate]);

    return (
        <Render<SkeletonProps>
            when={Boolean(cardBrandList && cardBrandList.length > 0)}
            loadingComponentProps={{ className: 'h-36 sm:h-56' }}
        >
            <FadeIn>
                <div className="pt-14 pb-8 text-2xl bg-pale-blue">
                    <div className="text-center mb-8 text-brand-dark px-4 font-semibold">
                        We thought you might also like these brands
                    </div>
                    <div className="flex flex-wrap gap-2 justify-center">
                        {cardBrandList.map((brand) => (
                            <BrandCardSlim key={brand.id} styles={{}} data={brand} />
                        ))}
                    </div>
                </div>
            </FadeIn>
        </Render>
    );
};
